import { ELanguage } from '../../Utils/constants'
import { objToQueryStr } from '../../Utils/urlBuilder'
import ModelError from '../model'
import {
    ATTACHED_RESUME_VALIDATE,
    BASIC_INFO,
    BASIC_INFO_V2,
    CHANGE_JOB_TITLE,
    CHANGE_PHONE_NUMBER,
    ENDPOINT_UPDATE_RESUME_FILE,
    JOBLEVEL,
    PROFILE_COOKIE,
    PROFILE_LEVEL,
    REFRESH_PROFILE,
    RESUME_SETTING_INFO,
    SEARCHABLE,
    SKILL_TAGS,
    UPDATE_RESUME_SETTING_INFO,
    USER_SKILL
} from './const'
import {
    EOnOffStatus,
    ERefreshProfile,
    IEditProfileParams,
    IUpdateBasicInfoRespone,
    IUploadFileParams,
    IValidateResumeParams,
    TFormUpdateBasicInfo,
    TJobLevel,
    TPayloadUpdateLatestSearchAble,
    TProfileLevel,
    TProfileSetting,
    TResponeJobLevels,
    TResponseEditProfile,
    TResponseUploadFile,
    TResponseValidateResume,
    TResumeSettingResponse,
    TSkill,
    TUserProfile
} from './types'
import { ISkill } from '../../JobMatchingAnalysis/type'
import { formatInvalidDate } from '../../Utils/time'

export async function changeJobTitle({
    baseUrl,
    accessToken,
    langCode,
    value
}: IEditProfileParams): Promise<TResponseEditProfile> {
    try {
        const response = await fetch(`${baseUrl}/${CHANGE_JOB_TITLE}`, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Accept-Language': langCode
            },
            body: JSON.stringify({
                jobTitle: value
            })
        })
        return await response.json()
    } catch (err) {
        console.error(err)
        return {}
    }
}

export async function changePhoneNumber({
    baseUrl,
    accessToken,
    langCode,
    value
}: IEditProfileParams): Promise<TResponseEditProfile> {
    try {
        const response = await fetch(`${baseUrl}/${CHANGE_PHONE_NUMBER}`, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Accept-Language': langCode
            },
            body: JSON.stringify({
                phone: value
            })
        })
        return await response.json()
    } catch (err) {
        console.error(err)
        return {}
    }
}

export const uploadFile = async ({
    accessToken,
    langCode,
    baseUrl,
    file,
    resumeId,
    apiUploadVersion,
    action
}: IUploadFileParams): Promise<TResponseUploadFile | ModelError> => {
    const errorSystem = new ModelError({
        data: file,
        error: {
            details: {
                ERROR_UPLOAD: langCode === ELanguage.EN ? "Can't not upload file" : 'Không thể tải tệp lên'
            },
            message: langCode === ELanguage.EN ? "Can't not upload file" : 'Không thể tải tệp lên',
            status: 500
        }
    })
    try {
        const formData = new FormData()
        formData.append('resume_file', file)
        if (resumeId && resumeId > 0) {
            formData.append('resume_id', resumeId.toString())
            // When resumeId is provided it always replaced
            formData.append('is_replaced', 'true')
        }
        // Action=1 param is used for by-pass old parsing cv flow
        if (action) {
            formData.append('action', action.toString())
        }

        const endPointUploadResumeFile = `api-gateway/${apiUploadVersion}/${ENDPOINT_UPDATE_RESUME_FILE}`
        const response = await fetch(`${baseUrl}/${endPointUploadResumeFile}`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Accept-Language': langCode
            },
            body: formData
        })

        const responeJson = await response.json()

        if (!responeJson.data) {
            if (responeJson.Message || responeJson.Status === 400) {
                return new ModelError({
                    data: file,
                    error: {
                        details: {
                            ERROR_UPLOAD: responeJson.Message
                        },
                        message: responeJson.Message,
                        status: responeJson.Status
                    }
                })
            }
            return errorSystem
        }

        return responeJson
    } catch (error) {
        console.error('apiDoUpload ', error)

        return errorSystem
    }
}

export const validateFile = async ({
    accessToken,
    langCode,
    baseUrl,
    file,
    userId,
    jobId
}: IValidateResumeParams): Promise<TResponseValidateResume | undefined> => {
    try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('jobId', jobId ? String(jobId) : '0')
        formData.append('jobseekerId', userId ? String(userId) : '0')
        const response = await fetch(`${baseUrl}/${ATTACHED_RESUME_VALIDATE}`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Accept-Language': langCode
            },
            body: formData
        })
        const data: TResponseValidateResume = await response.json()
        return data
    } catch (error) {
        console.error('apiDoValidate ', error)
        const data: TResponseValidateResume = {
            meta: {
                statusCode: 413,
                message:
                    langCode === ELanguage.VI
                        ? 'Hỗ trợ định dạng .doc, .docx, pdf có kích thước dưới 5120KB'
                        : '.doc, .docx and .pdf files that are less than 5120KB in size'
            }
        }
        return data
    }
}

export async function getProfileLevel(
    accessToken: string,
    langCode: string,
    baseUrl: string
): Promise<TProfileLevel | undefined> {
    try {
        const response = await fetch(`${baseUrl}/${PROFILE_LEVEL}`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Accept-Language': langCode
            }
        })
        const { data } = await response.json()
        return data
    } catch (err) {
        console.error(err)
        return
    }
}

export async function getResumeSettingInfo(
    accessToken: string,
    baseUrl: string,
    langCode?: ELanguage
): Promise<TResumeSettingResponse | null> {
    try {
        const response = await fetch(`${baseUrl}/${RESUME_SETTING_INFO}?date=${Date.now()}`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Accept-Language': langCode ?? ELanguage.VI
            }
        })
        return await response.json()
    } catch (err) {
        console.error(err)
        return null
    }
}

export const getJobLevelList = async (langCode: string, baseMsUrl: string): Promise<TJobLevel[] | null> => {
    try {
        const response = await fetch(`${baseMsUrl}/${JOBLEVEL}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': langCode
            }
        })
        const dataJs: TResponeJobLevels | null = await response.json()
        const relationships = dataJs?.data?.relationships
        if (relationships?.data && relationships?.data?.length > 0) {
            return relationships.data
        }
        return null
    } catch (err) {
        console.error(err)
        return null
    }
}

export const updateProfileCookie = async (
    baseUrl: string,
    accessToken: string,
    body?: TProfileSetting,
    langCode?: ELanguage
) => {
    try {
        const response = await fetch(`${baseUrl}/${PROFILE_COOKIE}`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Accept-Language': langCode ?? ELanguage.VI
            },
            body: JSON.stringify(body)
        })
        return await response.json()
    } catch (error) {
        console.error(error)
    }
}

export async function makeSearchable(
    baseUrl: string,
    accessToken: string,
    body: {
        resumeId: number
        searchAble: 0 | 1
    },
    langCode?: ELanguage
): Promise<
    | {
          code: number
          id: number
          message: string
      }
    | undefined
> {
    try {
        const response = await fetch(`${baseUrl}/${SEARCHABLE}`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Accept-Language': langCode ?? ELanguage.VI
            },
            body: JSON.stringify(body)
        })
        const data: {
            code: number
            id: number
            message: string
        } = await response.json()

        return data
    } catch (err) {
        console.error(err)
        return
    }
}

export const makeRefreshProfile = async (
    baseMsUrl: string,
    accessToken: string,
    body: {
        type: ERefreshProfile
        status: EOnOffStatus
    },
    langCode: ELanguage | undefined = ELanguage.VI
) => {
    try {
        const response = await fetch(`${baseMsUrl}/${REFRESH_PROFILE}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
                'Accept-Language': langCode
            },
            body: JSON.stringify(body)
        })
        const data: {
            code: number
            id: number
            message: string
        } = await response.json()
        return data
    } catch (e) {
        const error = {
            details: {
                RESFRESH_PROFILE: 'ERROR_RESFRESH_PROFILE'
            },
            message: 'bad request',
            status: 400
        }
        return new ModelError({ data: e, error: error })
    }
}

export async function getSkills(
    baseUrl: string,
    accessToken: string,
    searchKeywork: string,
    selectedSkills?: string[],
    limit?: number,
    lang?: ELanguage
): Promise<TSkill[] | undefined> {
    try {
        const queryParams = {
            query: searchKeywork,
            ...(!selectedSkills ? undefined : { selectedSkill: selectedSkills.join('|') }),
            ...(!limit ? undefined : { limit: limit })
        }

        const response = await fetch(`${baseUrl}/${SKILL_TAGS}?${objToQueryStr(queryParams)}`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Accept-Language': lang ?? ELanguage.VI
            }
        })
        if (response) {
            const jsonData = await response.json()
            return jsonData.data
        }
        throw response
    } catch (error) {
        console.log(error)
        return
    }
}

export async function updateBasicInfo(
    baseUrl: string,
    accessToken: string,
    body: TFormUpdateBasicInfo,
    langCode?: ELanguage
): Promise<IUpdateBasicInfoRespone | undefined> {
    try {
        const response = await fetch(`${baseUrl}/${BASIC_INFO}`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Accept-Language': langCode ?? ELanguage.VI
            },
            body: JSON.stringify(body)
        })
        const data: IUpdateBasicInfoRespone = await response.json()

        return data
    } catch (err) {
        console.error(err)
        return
    }
}

export async function updateLatestResumeSearchAble(
    accessToken: string,
    data: TPayloadUpdateLatestSearchAble,
    baseMsUrl?: string,
    language: string | undefined = ELanguage.VI
) {
    try {
        const response = await fetch(`${baseMsUrl}/${UPDATE_RESUME_SETTING_INFO}`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Accept-Language': language
            },
            body: JSON.stringify(data)
        })

        return await response.json()
    } catch (e) {
        console.error(e)
    }
}

export async function updateSkills(
    baseMsUrl: string,
    accessToken: string,
    body: ISkill,
    langCode: ELanguage,
    method: 'POST' | 'DELETE' | undefined = 'POST'
) {
    try {
        const response = await fetch(`${baseMsUrl}/${USER_SKILL}`, {
            method: method,
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Accept-Language': langCode ?? ELanguage.VI
            },
            body: JSON.stringify(body)
        })
        return await response.json()
    } catch (error) {
        console.error(error)
        return {
            errorCode: '',
            message: '',
            showMessage: true
        }
    }
}

export async function getUserProfile(
    accessToken: string,
    baseMsUrl: string,
    langCode?: ELanguage
): Promise<Partial<TUserProfile> | ModelError> {
    try {
        const response = await fetch(`${baseMsUrl}/${BASIC_INFO_V2}`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Accept-Language': langCode ?? ELanguage.VI
            }
        })
        const responseData: {
            data: {
                type?: string
                id?: number
                attributes?: Partial<TUserProfile>
            }
        } = await response.json()

        const userProfileInfo = responseData.data?.attributes

        if (userProfileInfo && userProfileInfo.birthday) {
            userProfileInfo.birthday = formatInvalidDate(userProfileInfo.birthday, 'YYYY-MM-DD HH:MM:SS')
        }

        if (!userProfileInfo) {
            return new ModelError({
                data: null,
                error: {
                    details: {
                        ERROR: responseData?.data ?? ''
                    },
                    message: response.statusText,
                    status: response.status
                }
            })
        }
        return userProfileInfo
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}
